import React from 'react';
import { Card, Input, Select, Button, Space, Tag } from 'antd';
import { DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, PlusOutlined } from '@ant-design/icons';
import type { NoteBlock } from './NoteBlockRenderer';

const { TextArea } = Input;

const BLOCK_TYPE_LABELS: Record<NoteBlock['type'], string> = {
  heading: 'Heading', paragraph: 'Paragraph', bullet_list: 'Bullet List',
  numbered_list: 'Numbered List', table: 'Table', callout: 'Callout', definition: 'Definition',
};

function emptyBlock(type: NoteBlock['type']): NoteBlock {
  switch (type) {
    case 'heading': return { type, text: '' };
    case 'paragraph': return { type, text: '' };
    case 'bullet_list': return { type, items: [''] };
    case 'numbered_list': return { type, items: [''] };
    case 'table': return { type, headers: ['', ''], rows: [['', '']] };
    case 'callout': return { type, style: 'info', text: '' };
    case 'definition': return { type, term: '', explanation: '' };
  }
}

// Structured editor: one small form per block type, rather than a raw JSON textarea —
// the point is a non-technical admin can edit this without knowing the schema.
const BlockEditorRow: React.FC<{
  block: NoteBlock; onChange: (b: NoteBlock) => void; onDelete: () => void;
  onMoveUp?: () => void; onMoveDown?: () => void;
}> = ({ block, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const header = (
    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
      <Tag color="blue">{BLOCK_TYPE_LABELS[block.type]}</Tag>
      <Space size="small">
        <Button size="small" icon={<ArrowUpOutlined />} onClick={onMoveUp} disabled={!onMoveUp} />
        <Button size="small" icon={<ArrowDownOutlined />} onClick={onMoveDown} disabled={!onMoveDown} />
        <Button size="small" danger icon={<DeleteOutlined />} onClick={onDelete} />
      </Space>
    </Space>
  );

  return (
    <Card size="small" style={{ marginBottom: 10 }} title={header}>
      {(block.type === 'heading' || block.type === 'paragraph') && (
        <TextArea
          rows={block.type === 'heading' ? 1 : 3}
          value={block.text}
          placeholder={block.type === 'heading' ? 'Section heading' : 'Paragraph text — supports $latex$'}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
        />
      )}

      {(block.type === 'bullet_list' || block.type === 'numbered_list') && (
        <Space direction="vertical" style={{ width: '100%' }}>
          {block.items.map((item, i) => (
            <Space key={i} style={{ width: '100%' }}>
              <Input
                value={item}
                placeholder={`Point ${i + 1}`}
                onChange={(e) => {
                  const items = [...block.items]; items[i] = e.target.value;
                  onChange({ ...block, items });
                }}
              />
              <Button
                size="small" danger icon={<DeleteOutlined />}
                onClick={() => onChange({ ...block, items: block.items.filter((_, ix) => ix !== i) })}
              />
            </Space>
          ))}
          <Button size="small" icon={<PlusOutlined />} onClick={() => onChange({ ...block, items: [...block.items, ''] })}>
            Add point
          </Button>
        </Space>
      )}

      {block.type === 'table' && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space style={{ width: '100%' }}>
            {block.headers.map((h, ci) => (
              <Input
                key={ci} value={h} placeholder={`Header ${ci + 1}`} style={{ fontWeight: 600 }}
                onChange={(e) => {
                  const headers = [...block.headers]; headers[ci] = e.target.value;
                  onChange({ ...block, headers });
                }}
              />
            ))}
            <Button size="small" icon={<PlusOutlined />} onClick={() => onChange({
              ...block, headers: [...block.headers, ''], rows: block.rows.map((r) => [...r, '']),
            })} title="Add column" />
          </Space>
          {block.rows.map((row, ri) => (
            <Space key={ri} style={{ width: '100%' }}>
              {row.map((cell, ci) => (
                <Input
                  key={ci} value={cell}
                  onChange={(e) => {
                    const rows = block.rows.map((r) => [...r]); rows[ri][ci] = e.target.value;
                    onChange({ ...block, rows });
                  }}
                />
              ))}
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onChange({ ...block, rows: block.rows.filter((_, ix) => ix !== ri) })} />
            </Space>
          ))}
          <Button size="small" icon={<PlusOutlined />} onClick={() => onChange({ ...block, rows: [...block.rows, block.headers.map(() => '')] })}>
            Add row
          </Button>
        </Space>
      )}

      {block.type === 'callout' && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            value={block.style} style={{ width: 160 }}
            options={[{ value: 'info', label: 'Info' }, { value: 'important', label: 'Important' }, { value: 'tip', label: 'Tip' }]}
            onChange={(style) => onChange({ ...block, style })}
          />
          <TextArea rows={2} value={block.text} placeholder="Callout text" onChange={(e) => onChange({ ...block, text: e.target.value })} />
        </Space>
      )}

      {block.type === 'definition' && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input value={block.term} placeholder="Term" onChange={(e) => onChange({ ...block, term: e.target.value })} />
          <TextArea rows={2} value={block.explanation} placeholder="Explanation" onChange={(e) => onChange({ ...block, explanation: e.target.value })} />
        </Space>
      )}
    </Card>
  );
};

const ADD_TYPES: NoteBlock['type'][] = ['heading', 'paragraph', 'bullet_list', 'numbered_list', 'table', 'callout', 'definition'];

const NoteBlocksEditor: React.FC<{ blocks: NoteBlock[]; onChange: (blocks: NoteBlock[]) => void }> = ({ blocks, onChange }) => {
  const update = (i: number, b: NoteBlock) => onChange(blocks.map((x, ix) => (ix === i ? b : x)));
  const remove = (i: number) => onChange(blocks.filter((_, ix) => ix !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks]; [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      {blocks.map((b, i) => (
        <BlockEditorRow
          key={i} block={b}
          onChange={(nb) => update(i, nb)}
          onDelete={() => remove(i)}
          onMoveUp={i > 0 ? () => move(i, -1) : undefined}
          onMoveDown={i < blocks.length - 1 ? () => move(i, 1) : undefined}
        />
      ))}
      <Space wrap>
        {ADD_TYPES.map((t) => (
          <Button key={t} size="small" icon={<PlusOutlined />} onClick={() => onChange([...blocks, emptyBlock(t)])}>
            {BLOCK_TYPE_LABELS[t]}
          </Button>
        ))}
      </Space>
    </div>
  );
};

export default NoteBlocksEditor;
